import { MESSAGE_TIMEOUT_ERROR } from '@ds/swymkit/config'
import { message } from 'vuekit'

import type { ContentReaderConfiguration } from '~/features-swym/widgets/content-reader/interfaces/content-reader'
import {
  ContentReaderDropZone,
  ContentReaderMode,
} from '~/features-swym/widgets/content-reader/interfaces/content-reader'
import {
  extractTypeAndIdArray,
  processContentReaderWidgetData,
} from '~/features-swym/widgets/content-reader/utils/content-reader-query'

import { IContentModel } from '~/features-shared/content/interfaces/content-model'
import { getType } from '~/features-shared/content/utils/content-type-utils'
import { assertWidget } from '~/features-shared/libs/utils/assert'
import { extractDataFromi3DxUri } from '~/features-shared/libs/utils/i3dx'
import { get3DXContentFromX3DContentId, isContent3DX } from '~/features-shared/media-upload/utils/drive'
import { getTenantIdFromUrlPreference } from '~/features-shared/widget/utils/setup-widget'

import { useTranslations } from '~/features-generic/translations/composables/translations'

/** Defines data extracted from dropped content in content reader widget. */
interface DroppedData {
  /** Id of the content */
  contentId: string
  /** Type of the content */
  contentType: string
  /** Tenant Id */
  tenant: string
}

/**
 * Replace contents in the app.
 * @param contentData Content data
 * @param contentData.contentId Id of the new content
 * @param contentData.contentType Type od new content
 */
function replaceContent(contentData: { contentId: string, contentType: string }): void {
  return window.widget?.setValues({
    contentId: contentData.contentId,
    contentType: contentData.contentType,
    multiContentIds: '',
  })
}

function addinMultiContentMode(contentId: string, contentType: string) {
  assertWidget(window.widget)
  const _widget = window.widget
  let firstTypeInPref
  let multiContentIds = (_widget.getValue('multiContentIds') as string).trim()
  // re = /(post|media|question|idea|wiki)\s*:\s*(?:[0-9a-zA-Z-_]+,?)+$/gi,
  // matches = re.exec(multiContentPrefValue);
  // pref=> post:1,2,3,media:4,5,idea:6
  // split into [post:1][2][3][media:4][5][idea:6]
  const contentParts = multiContentIds.split(',')

  // temp = contentParts[0].split(':'); //split into [post][1]
  const temp = contentParts[0].split(/:(.+)/) // wedo contains ":" in their ID !
  temp.pop()
  if (temp.length === 2) {
    firstTypeInPref = temp[0]
    if (
      ['post', 'media', 'question', 'idea', 'wiki', 'survey', 'wedo', 'social-event'].includes(firstTypeInPref)
    ) {
      if (firstTypeInPref === contentType)
        multiContentIds = `${contentType}:${contentId},${multiContentIds.slice(multiContentIds.indexOf(':') + 1)}`
      else
        multiContentIds = `${contentType}:${contentId},${multiContentIds}`
    }
  }

  _widget.setValue('multiContentIds', multiContentIds)
}

function findItemAndRemove(data: {
  contentType: string
  contentId: string
  idArr: string[]
  typeArr: string[]
}) {
  const index = data.idArr.findIndex((id, _index) => data.contentId === id && data.contentType === data.typeArr[_index])
  // Not found
  if (index === -1)
    return ''

  // item is already in the list,
  let prefString = ''
  data.idArr.splice(index, 1)
  data.typeArr.splice(index, 1)
  /**
   *  MulticontentIds preference is stored in following ways:
   *   case 1: when adjecent contents are of same type:
   *           post:content1,content2,content3
   *   case 2: when adjecent content are of different types:
   *           post:content1,media:content2,wedo:content3
   */
  data.typeArr.reduce((
    previousValue,
    currentValue,
    index,
  ) => {
    /**
     * If adjecent contents are of same type, just append the content IDs comma seperated.
     *  "Post:content1" + ",content2" => "Post:content1,content2"
     */
    if (previousValue === currentValue)
      prefString += `,${data.idArr[index]}`

    /**
     * If adjecent contents are of different types, construct type:Id format.
     *  "Post:content1" + "media:content2" => "Post:content1,media:content2"
     */
    else
      prefString += `${(index === 0 ? '' : ',') + currentValue}:${data.idArr[index]}`
    return currentValue
  }, '')
  return prefString
}

// eslint-disable-next-line max-statements
export function removeInMultipleContentMode(contentData: { contentType: string, contentId: string }) {
  assertWidget(window.widget)
  const _widget = window.widget
  let multiContentIds = (_widget.getValue('multiContentIds') as string).trim()
  if (multiContentIds.includes(contentData.contentId)) {
    const { contentIdArray, contentTypeArray } = extractTypeAndIdArray(multiContentIds)
    const result = findItemAndRemove({
      ...contentData,
      typeArr: contentTypeArray,
      idArr: contentIdArray,
    })
    if (!result)
      return
    multiContentIds = result
  }
  if (multiContentIds[multiContentIds.length - 1] === ',')
    multiContentIds = multiContentIds.slice(0, -1)

  /**
   *  Extract firstId from multicontentIds.
   * Second item "isMultiContent" determines whether widget is in single
   * or multi-content mode
   */
  const [firstId, isMultiContent] = multiContentIds.split(',')
  if (isMultiContent) {
    _widget.setValues({
      contentType: 'multi',
      contentId: '',
      multiContentIds,
    })
    return
  }

  /**
   * Extract type and Id from preference for single content mode.
   */
  const [contentType, contentId] = firstId.split(/:(.+)/) // wedo contains ":" in their ID !
  if (contentType && contentId) {
    _widget.setValues({
      contentType,
      contentId,
      multiContentIds: '',
    })
  }
}

/* eslint-disable complexity */
function addContent(contentData: { contentId: string, contentType: string, tenant: string }) {
  assertWidget(window.widget)
  const _widget = window.widget
  const contentType = _widget.getValue('contentType') as string
  const contentId = _widget.getValue('contentId') as string
  const multiContentIds = (_widget.getValue('multiContentIds') as string).trim()
  const readerMode = contentType === 'multi'
    ? ContentReaderMode.MULTI_CONTENT
    : (contentId.length ? ContentReaderMode.SINGLE_CONTENT : ContentReaderMode.NO_CONTENT)

  // Determine if content is already present in CRA preferences
  if ((contentData.contentId === contentId || (multiContentIds && multiContentIds.includes(contentData.contentId)))
    && getTenantIdFromUrlPreference(_widget) === contentData.tenant) {
    const { $i18n, promise } = useTranslations('content-reader')
    promise.then(() => {
      message.create({
        text: $i18n('elementAlreadyExists'),
        color: 'error',
        timeout: MESSAGE_TIMEOUT_ERROR,
      })
    })
    return
  }
  switch (readerMode) {
    case ContentReaderMode.NO_CONTENT:
      replaceContent({ contentId: contentData.contentId, contentType: contentData.contentType })
      break
    case ContentReaderMode.SINGLE_CONTENT:
      _widget.setValues({
        contentType: 'multi',
        contentId: '',
        multiContentIds: contentData.contentType === contentType
          ? `${contentData.contentType}:${contentData.contentId},${contentId}`
          : `${contentData.contentType}:${contentData.contentId},${contentType}:${contentId}`,
      })
      break
    case ContentReaderMode.MULTI_CONTENT:
      addinMultiContentMode(contentData.contentId, contentData.contentType)
      break
  }
}

function removeContent(contentData: { contentId: string, contentType: string }) {
  assertWidget(window.widget)
  const _widget = window.widget
  const contentType = _widget.getValue('contentType') as string
  const contentId = _widget.getValue('contentId') as string
  const readerMode = contentType === 'multi'
    ? ContentReaderMode.MULTI_CONTENT
    : (contentId.length ? ContentReaderMode.SINGLE_CONTENT : ContentReaderMode.NO_CONTENT)
  // Cannot remove in single-content mode
  if (readerMode === ContentReaderMode.SINGLE_CONTENT)
    return
  removeInMultipleContentMode(contentData)
}

// eslint-disable-next-line max-statements
function extractContentDataFromDragData(dragData: string): DroppedData | undefined {
  const _widget = window.widget
  const { $i18n, promise } = useTranslations('content-reader')
  try {
    const x3DContentId = get3DXContentFromX3DContentId(dragData)
    const x3DContentIdItems = x3DContentId?.data.items
    if (isContent3DX(x3DContentId) && x3DContentIdItems && x3DContentIdItems.length) {
      const item = x3DContentIdItems[0]
      const tenant = item.envId
      if (_widget && getTenantIdFromUrlPreference(_widget) !== tenant) {
      // Cross tenant Drop disabled
        promise.then(() => {
          message.create({
            text: $i18n('crossPlatformContentNotSupported'),
            color: 'error',
            timeout: MESSAGE_TIMEOUT_ERROR,
          })
        })
        return
      }
      /**
       * if i3dx is present, use it to extract content type.
       * Presence of i3dx uri indicates that 3DXContent version is 2.1.
       */
      if (item.i3dx) {
        const { objectType, objectId } = extractDataFromi3DxUri(item.i3dx)
        // eslint-disable-next-line max-depth
        if (objectType) {
          return {
            contentId: objectId.split(':')[4] || objectId,
            contentType: objectType === IContentModel.Question ? 'question' : objectType,
            tenant,
          }
        }
      }
      const contentType = getType(item) || ''
      let contentId = ''
      switch (contentType) {
        case 'social-event':
          contentId = item.objectId
          break
        default:
          contentId = item.objectId.split(':')[4] || item.objectId // for 3dplan theobject id must be used as it is
      }
      return {
        contentId,
        contentType,
        tenant,
      }
    }
    else {
      promise.then(() => {
        message.create({
          text: $i18n('objectTypeNotSupported'),
          color: 'error',
          timeout: MESSAGE_TIMEOUT_ERROR,
        })
      })
    }
  }
  catch (err) {
    promise.then(() => {
      message.create({
        text: $i18n('objectTypeNotSupported'),
        color: 'error',
        timeout: MESSAGE_TIMEOUT_ERROR,
      })
    })
  }
}

export function handleContentReaderWidgetDropEvent(data: { dragData: string, zone: ContentReaderDropZone }) {
  assertWidget(window.widget)
  const _widget = window.widget
  // Standalone case - Handle preferences locally
  const contentData = extractContentDataFromDragData(data.dragData)
  if (_widget && contentData) {
    switch (data.zone) {
      case ContentReaderDropZone.ADD:
        addContent(contentData)
        break
      case ContentReaderDropZone.REMOVE:
        removeContent(contentData)
        break
      case ContentReaderDropZone.REPLACE:
        replaceContent(contentData)
        break
    }
  }
}

export function getContentReaderData(): Promise<ContentReaderConfiguration> {
  assertWidget(window.widget)
  const _widget = window.widget
  if (_widget) {
    const result: ContentReaderConfiguration = processContentReaderWidgetData(_widget)
    // replace question with iquestion
    if (result.contentTypeArray) {
      result.contentTypeArray = result.contentTypeArray.map((type) => {
        if (type === 'question')
          return IContentModel.Question
        if (type === 'social-event')
          return IContentModel.Conference
        return type
      })
    }
    return Promise.resolve(result)
  }

  // Dev Mode data
  return Promise.resolve({
    readerMode: ContentReaderMode.NO_CONTENT,
    refineUsingQuery: '',
    contentIdArray: [],
    contentTypeArray: [],
    widgetId: '',
    isReadOnly: false,
  })
}
